/**
 * Drive (云盘) API client — folders + files for the CRM customer file cabinet
 * (and, later, the knowledge base).
 *
 * Stays on raw authFetch (not hc): uploads PUT raw bytes and downloads stream a
 * blob, which the hc JSON conventions don't cover. The JSON endpoints ride the
 * same authFetch for one cohesive module.
 */

import { authFetch } from '../auth';

export type DriveScope = 'crm' | 'kb';

export interface DriveFolder {
  id: number;
  scope: DriveScope;
  parent_id: number | null;
  name: string;
  visibility: 'team' | 'private' | null;
  owner_user_id: string | null;
  company_id: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DriveFile {
  id: number;
  scope: DriveScope;
  folder_id: number | null;
  name: string;
  cos_key: string;
  content_type: string | null;
  size: number;
  status: 'pending' | 'active' | 'deleted';
  visibility: 'team' | 'private' | null;
  owner_user_id: string | null;
  company_id: number | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Owner keys identifying which scope/container a node belongs to. */
export type DriveOwner = { scope: 'crm'; company_id: number } | { scope: 'kb'; visibility: 'team' | 'private' };

async function jsonReq<T>(url: string, method: string, body?: unknown): Promise<T> {
  const res = await authFetch(url, {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `请求失败 (${res.status})`);
  }
  return res.json() as Promise<T>;
}

function ownerQuery(owner: DriveOwner): string {
  return owner.scope === 'crm' ? `scope=crm&company_id=${owner.company_id}` : `scope=kb&visibility=${owner.visibility}`;
}

export async function listDriveFolders(owner: DriveOwner, parentId: number | null): Promise<DriveFolder[]> {
  const q = `${ownerQuery(owner)}${parentId != null ? `&parent_id=${parentId}` : ''}`;
  const { folders } = await jsonReq<{ folders: DriveFolder[] }>(`/api/drive/folders?${q}`, 'GET');
  return folders;
}

export async function listDriveFiles(owner: DriveOwner, folderId: number | null): Promise<DriveFile[]> {
  const q = `${ownerQuery(owner)}${folderId != null ? `&folder_id=${folderId}` : ''}`;
  const { files } = await jsonReq<{ files: DriveFile[] }>(`/api/drive/files?${q}`, 'GET');
  return files;
}

export async function driveBreadcrumb(folderId: number): Promise<DriveFolder[]> {
  const { folders } = await jsonReq<{ folders: DriveFolder[] }>(`/api/drive/breadcrumb?folder_id=${folderId}`, 'GET');
  return folders;
}

export async function createDriveFolder(
  owner: DriveOwner,
  parentId: number | null,
  name: string,
): Promise<DriveFolder> {
  const { folder } = await jsonReq<{ folder: DriveFolder }>('/api/drive/folders', 'POST', {
    ...owner,
    parent_id: parentId ?? undefined,
    name,
  });
  return folder;
}

export async function deleteDriveFolder(id: number): Promise<void> {
  await jsonReq(`/api/drive/folders/${id}`, 'DELETE');
}

export async function deleteDriveFile(id: number): Promise<void> {
  await jsonReq(`/api/drive/files/${id}`, 'DELETE');
}

/**
 * Upload one file: init → upload bytes → (presigned: confirm). Works in both
 * backends — direct presigned PUT to COS, or proxy PUT through the API on local.
 */
export async function uploadDriveFile(owner: DriveOwner, folderId: number | null, file: File): Promise<void> {
  const init = await jsonReq<{ file_id: number; mode: 'cos' | 'proxy'; upload_url: string }>(
    '/api/drive/files/init',
    'POST',
    {
      ...owner,
      folder_id: folderId ?? undefined,
      name: file.name,
      size: file.size,
      content_type: file.type || undefined,
    },
  );

  if (init.mode === 'cos') {
    const put = await fetch(init.upload_url, { method: 'PUT', body: file });
    if (!put.ok) throw new Error('上传到对象存储失败');
    await jsonReq(`/api/drive/files/${init.file_id}/complete`, 'POST', {});
  } else {
    const put = await authFetch(init.upload_url, {
      method: 'PUT',
      headers: file.type ? { 'content-type': file.type } : undefined,
      body: file,
    });
    if (!put.ok) throw new Error('上传失败');
  }
}

/**
 * Download a file. Hits the authed content endpoint and saves the blob — this
 * works for both backends (the endpoint either streams locally or 302s to a
 * short-lived presigned COS URL, which fetch follows).
 */
export async function downloadDriveFile(file: DriveFile): Promise<void> {
  const res = await authFetch(`/api/drive/files/${file.id}/content?download=1`);
  if (!res.ok) throw new Error('下载失败');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Human-readable byte size. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
